<template>
    <div
      v-if="$parent.isSmartMode"
      class="left-sidebar"
      :class="{ collapsed: $parent.sidebarCollapsed, 'sidebar-hidden': $parent.hideSidebar }"
    >
      <!-- Dashboard 路由：el-menu 风格 -->
      <template v-if="$parent.isDashboardRoute">
        <div class="sidebar-content dashboard-menu">
          <el-menu
            ref="dashboardMenu"
            :default-openeds="$parent.dynamicMenuGroups.map((_, i) => 'group-' + i)"
            class="dashboard-el-menu"
          >
            <el-submenu
              v-for="(group, gi) in $parent.dynamicMenuGroups"
              :key="group.id || gi"
              :index="'group-' + gi"
            >
              <template slot="title">
                <img
                  v-if="$parent.getMenuIcon(group.resourceIcon).type === 'url'"
                  :src="$parent.getMenuIcon(group.resourceIcon).value"
                  class="menu-icon-img"
                />
                <img
                  v-else-if="$parent.getMenuIcon(group.resourceIcon).type === 'local'"
                  :src="$parent.getLocalIcon($parent.getMenuIcon(group.resourceIcon).value)"
                  class="menu-icon-img"
                />
                <i v-else class="el-icon-menu"></i>
                <span>{{ group.name }}</span>
              </template>
              <el-menu-item
                v-for="item in group.children"
                :key="item.id"
                :index="item.permission || String(item.id || item.name)"
                :disabled="!item._enabled"
                @click="$parent.handleMenuItemClick(item)"
              >
                <img
                  v-if="$parent.getMenuIcon(item.resourceIcon).type === 'url'"
                  :src="$parent.getMenuIcon(item.resourceIcon).value"
                  class="menu-icon-img"
                />
                <img
                  v-else-if="$parent.getMenuIcon(item.resourceIcon).type === 'local'"
                  :src="$parent.getLocalIcon($parent.getMenuIcon(item.resourceIcon).value)"
                  class="menu-icon-img"
                />
                <i v-else class="el-icon-document"></i>
                <span>{{ item.name }}</span>
                <div class="disable-tag" v-if="!item._enabled">待上线</div>
              </el-menu-item>
            </el-submenu>
          </el-menu>
          <div class="sidebar-section" v-if="!$parent.sidebarCollapsed">
            <div class="section-header">
              <span class="menu-label">{{ $t("workbench.sessionHistory") }}</span>
              <div class="section-header-right">
                <el-checkbox
                  v-if="$parent.filteredSessions.length > 0"
                  class="select-all-checkbox"
                  :value="$parent.isAllSessionsSelected"
                  :indeterminate="$parent.isSomeSessionsSelected"
                  @change="$parent.handleToggleSelectAll"
                >全选</el-checkbox>
                <el-button
                  v-if="$parent.selectedSessionKeys.length > 0"
                  size="mini"
                  type="danger"
                  plain
                  @click="$parent.handleBatchDelete"
                  >批量删除({{ $parent.selectedSessionKeys.length }})</el-button
                >
                <i
                  v-if="$parent.sessionsLoading"
                  class="el-icon-loading sessions-loading-icon"
                ></i>
              </div>
            </div>
            <div class="history-list">
              <div v-if="$parent.sessionsLocked" class="history-list-mask">
                <i class="el-icon-loading"></i>
                <span>{{ $t("workbench.loading") }}</span>
              </div>
              <div
                v-if="$parent.sessionsLoading && $parent.filteredSessions.length === 0"
                class="history-loading"
              >
                <i class="el-icon-loading"></i>
                <span>{{ $t("workbench.loading") }}</span>
              </div>
              <div :class="{ 'history-items-disabled': $parent.sessionsLocked }">
                <div
                  class="history-item"
                  v-for="session in $parent.filteredSessions"
                  :key="session.globalKey || session.key"
                  :title="session.label || session.key"
                  :class="{ active: $parent.currentSessionKey === session.key }"
                  @click="$parent.handleSwitchSession(session.key)"
                >
                  <span
                    class="session-checkbox-wrapper"
                    :class="{ checked: $parent.selectedSessionKeys.indexOf(session.key) !== -1 }"
                    @click.stop="$parent.handleToggleSessionCheckbox(session.key)"
                  >
                    <el-checkbox
                      :value="$parent.selectedSessionKeys.indexOf(session.key) !== -1"
                      class="session-checkbox"
                    ></el-checkbox>
                  </span>
                  <template v-if="$parent.editingSessionKey === session.key">
                    <input
                      ref="editInput"
                      class="session-edit-input"
                      :value="session.label"
                      @keyup.enter="$parent.handleSaveLabel(session.key, $event)"
                      @keyup.esc="$parent.editingSessionKey = null"
                      @blur="$parent.handleSaveLabel(session.key, $event)"
                      @click.stop
                    />
                  </template>
                  <template v-else>
                    <span class="session-agent-badge" v-if="session.agentName">{{
                      $parent.getBadgeLabel(session)
                    }}</span>
                    <span class="session-title">{{ formatSessionTitle(session.label || session.key) }}</span>
                    <el-dropdown
                      trigger="click"
                      @command="(cmd) => $parent.handleSessionCommand(cmd, session)"
                    >
                      <i class="el-icon-more session-more" @click.stop></i>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item icon="el-icon-edit" command="edit">{{
                          $t("workbench.edit")
                        }}</el-dropdown-item>
                        <el-dropdown-item icon="el-icon-delete" command="delete">{{
                          $t("workbench.delete")
                        }}</el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </div>
                <div class="noMessages" v-if="$parent.filteredSessions.length === 0">
                  <img src="@/assets/imgs/workbench/noMessages.png" />
                  <div class="noMessagesText">您还没有发起过任何对话</div>
                  <div class="noMessagesBtn" @click="$parent.openDrawer" v-if="$parent.useDrawer">立即对话</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- 非 dashboard 路由：原有卡片风格 -->
      <template v-else>
        <div class="sidebar-header">
          <div class="sidebar-title">{{ $t("workbench.digitalOrgTitle") }}</div>
          <div class="sidebar-search" v-if="!$parent.sidebarCollapsed">
            <el-input
              size="small"
              :placeholder="$t('workbench.searchAgentPlaceholder')"
              suffix-icon="el-icon-search"
              v-model="searchAgentText"
            >
            </el-input>
          </div>
        </div>
        <div class="sidebar-content">
          <!-- 根据 appDepthMenu 动态渲染 -->
          <template v-for="(group, gi) in $parent.dynamicMenuGroups">
          <div
            :key="group.id || gi"
            class="sidebar-section"
            v-if="!$parent.sidebarCollapsed"
          >
            <div class="section-header">
              <span class="menu-label">{{ group.name }}</span>
            </div>
            <div class="sidebar-card-list">
              <div
                v-for="item in group.children"
                :key="item.id"
                class="sidebar-card"
                :class="{
                  active: $parent.selectedCardId === item.permission || $parent.selectedCardId === item.id || $parent.selectedCardId === (item._matchItem && String(item._matchItem.id)),
                  disabled: !item._enabled,
                }"
                @click="$parent.handleMenuItemClick(item)"
              >
                <div class="sidebar-card-logo">
                  <img
                    v-if="item.permission === 'digital_plaza'"
                    src="@/assets/imgs/workbench/digitalSquare.png"
                    class="menu-card-icon"
                  />
                  <img
                    v-else-if="item.permission === 'agent_plaza'"
                    src="@/assets/imgs/workbench/agentSquare.png"
                    class="menu-card-icon"
                  />
                  <img
                    v-else-if="item._matchItem && item._matchItem.logo"
                    :src="item._matchItem.logo"
                    class="menu-card-icon"
                  />
                  <i
                    v-else-if="item._matchItem && item._matchItem.icon"
                    :class="item._matchItem.icon"
                    style="font-size: 22px"
                  ></i>
                  <i v-else class="el-icon-menu" style="font-size: 24px"></i>
                </div>
                <div class="sidebar-card-info">
                  <div class="sidebar-card-title">{{ item.name }}</div>
                </div>
              </div>
            </div>
          </div>
          </template>

          <!-- 会话记录 -->
          <div class="sidebar-section" v-if="!$parent.sidebarCollapsed">
            <div class="section-header">
              <span class="menu-label">{{ $t("workbench.sessionHistory") }}</span>
              <div class="section-header-right">
                <el-checkbox
                  v-if="$parent.filteredSessions.length > 0"
                  class="select-all-checkbox"
                  :value="$parent.isAllSessionsSelected"
                  :indeterminate="$parent.isSomeSessionsSelected"
                  @change="$parent.handleToggleSelectAll"
                >全选</el-checkbox>
                <el-button
                  v-if="$parent.selectedSessionKeys.length > 0"
                  size="mini"
                  type="danger"
                  plain
                  @click="$parent.handleBatchDelete"
                  >批量删除({{ $parent.selectedSessionKeys.length }})</el-button
                >
                <i
                  v-if="$parent.sessionsLoading"
                  class="el-icon-loading sessions-loading-icon"
                ></i>
              </div>
            </div>
            <div class="history-list">
              <div v-if="$parent.sessionsLocked" class="history-list-mask">
                <i class="el-icon-loading"></i>
                <span>{{ $t("workbench.loading") }}</span>
              </div>
              <div
                v-if="$parent.sessionsLoading && $parent.filteredSessions.length === 0"
                class="history-loading"
              >
                <i class="el-icon-loading"></i>
                <span>{{ $t("workbench.loading") }}</span>
              </div>
              <div :class="{ 'history-items-disabled': $parent.sessionsLocked }">
                <div
                  class="history-item"
                  v-for="session in $parent.filteredSessions"
                  :key="session.globalKey || session.key"
                  :title="session.label || session.key"
                  :class="{ active: $parent.currentSessionKey === session.key }"
                  @click="$parent.handleSwitchSession(session.key)"
                >
                  <span
                    class="session-checkbox-wrapper"
                    :class="{ checked: $parent.selectedSessionKeys.indexOf(session.key) !== -1 }"
                    @click.stop="$parent.handleToggleSessionCheckbox(session.key)"
                  >
                    <el-checkbox
                      :value="$parent.selectedSessionKeys.indexOf(session.key) !== -1"
                      class="session-checkbox"
                    ></el-checkbox>
                  </span>
                  <template v-if="$parent.editingSessionKey === session.key">
                    <input
                      ref="editInput"
                      class="session-edit-input"
                      :value="session.label"
                      @keyup.enter="$parent.handleSaveLabel(session.key, $event)"
                      @keyup.esc="$parent.editingSessionKey = null"
                      @blur="$parent.handleSaveLabel(session.key, $event)"
                      @click.stop
                    />
                  </template>
                  <template v-else>
                    <span class="session-agent-badge" v-if="session.agentName">{{
                      $parent.getBadgeLabel(session)
                    }}</span>
                    <span class="session-title">{{ formatSessionTitle(session.label || session.key) }}</span>
                    <el-dropdown
                      trigger="click"
                      @command="(cmd) => $parent.handleSessionCommand(cmd, session)"
                    >
                      <i class="el-icon-more session-more" @click.stop></i>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item icon="el-icon-edit" command="edit">{{
                          $t("workbench.edit")
                        }}</el-dropdown-item>
                        <el-dropdown-item icon="el-icon-delete" command="delete">{{
                          $t("workbench.delete")
                        }}</el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </div>
                <div class="noMessages" v-if="$parent.filteredSessions.length === 0">
                  <img src="@/assets/imgs/workbench/noMessages.png" />
                  <div class="noMessagesText">您还没有发起过任何对话</div>
                  <div class="noMessagesBtn" @click="$parent.openDrawer" v-if="$parent.useDrawer">立即对话</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="sidebar-footer"></div>
      </template>
    </div>
</template>

<script>
import { stripAttachmentMarker } from '@/utils/markdownRenderer'

export default {
  name: 'LeftSidebar',
  data() {
    return {
      searchAgentText: ''
    }
  },
  methods: {
    // 会话标题精简：[附件]文件名(路径:xxx) → 📄 文件名
    formatSessionTitle(label) {
      return stripAttachmentMarker(label)
    }
  }
}
</script>

<style lang="scss" scoped>
/* ========== 左侧菜单 ========== */
.left-sidebar {
  position: relative;
  background: #ffffff;
  border-radius: 8px 0px 0px 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  transition: width 0.3s ease;

  &.collapsed {
    .sidebar-header {
      padding: 16px 8px;
    }

    .sidebar-content {
      padding: 16px 8px;
    }

    .menu-item {
      justify-content: center;
      padding: 10px;
    }
  }

  &.sidebar-hidden {
    overflow: hidden;
    padding: 0;
    margin: 0;
    border: none;
    min-width: 0;
  }
}

.sidebar-header {
  padding: 24px 16px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-title {
  font-weight: 400;
  font-size: 12px;
  color: #595959;
  text-align: left;
  line-height: 1;
}

.sidebar-search {
  width: 100%;
}

.sidebar-search .el-input__inner {
  height: 32px;
  line-height: 32px;
  background: #f5f5f5;
  border-color: transparent;
  border-radius: 6px;
}

.sidebar-search .el-input__inner:focus {
  background: #fff;
  border-color: #4a6cf7;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.sidebar-content:hover {
  scrollbar-color: rgba(148, 163, 184, 0.5) transparent;
}

.sidebar-content::-webkit-scrollbar {
  width: 4px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 4px;
}

.sidebar-content:hover::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.5);
}

.sidebar-section {
  padding: 12px 16px;
}

.sidebar-section .section-header {
  margin-bottom: 8px;
}

.sidebar-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sidebar-card {
  width: 100%;
  height: 64px;
  background: #ffffff;
  border: 1px solid #edf1fb;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;
}

.sidebar-card:hover,
.sidebar-card.active {
  background-image: linear-gradient(90deg, #d7dbfd 0%, #f6f7ff 23%, #dbdffd 100%);
  box-shadow: inset 0 0 8px 0 #ffffff;
}

.sidebar-card::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 8px;
  padding: 1px;
  background: linear-gradient(-49deg, #7a63fe 0%, #4a64f4 33%, #4d7efd 66%, #4eb9ff 100%);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.sidebar-card:hover::before,
.sidebar-card.active::before {
  opacity: 1;
}

.sidebar-card.disabled {
  cursor: default;
  opacity: 0.6;
}

.sidebar-card.disabled:hover {
  border-color: #edf1fb;
  box-shadow: none;
}

.sidebar-card-logo {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.sidebar-card-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-card-logo i {
  font-size: 20px;
  color: #999;
}

.sidebar-card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sidebar-card-title {
  font-size: 14px;
  color: #333333;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-card-desc {
  font-size: 12px;
  color: #595959;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-card-status {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 2px;
  background: #e4e8fc;
  box-shadow: inset 0 0 4px 0 #ffffffcc;
  border-radius: 8px;
  padding: 2px 5px;
  font-size: 12px;
  color: #4a5ff3;
  line-height: 1;
  transition: all 0.2s ease;
}

.sidebar-card:hover .sidebar-card-status,
.sidebar-card.active .sidebar-card-status {
  background: #ffffff;
  box-shadow: 0 2px 4px 0 #4a5ff333;
  color: #4a5ff3;
}

.status-dot {
  font-size: 10px;
  line-height: 1;
}

/* ========== 抽屉模式：对话按钮 ========== */
.session-btn {
  cursor: pointer;
  margin-left: 4px;
  padding: 1px 5px;
  border-radius: 4px;
  // background: #4A5FF3;
  // color: #fff !important;
  font-size: 11px;
  line-height: 1.4;
}

// .session-btn:hover {
//   background: #3a4fd6;
// }

/* 抽屉模式：数字人广场卡片箭头 */
.card-arrow {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  font-size: 14px;
  color: #bfc6d4;
  transition: transform 0.2s ease, color 0.2s ease;
}

.sidebar-card:hover .card-arrow {
  transform: translateY(-50%) translateX(3px);
  color: #4a5ff3;
}

/* 对话按钮 hover 时显示（仅抽屉模式） */
.sidebar-card .sidebar-card-status:has(.session-btn) {
  display: none;
}

.sidebar-card:hover .sidebar-card-status:has(.session-btn) {
  display: flex;
}

.menu-section {
  padding-bottom: 18px;
  padding-top: 16px;
  padding: 16px;
}

.menu-section:nth-child(1) {
  border-bottom: 1px dashed #edf1fb;
}

.menu-label {
  font-size: 12px;
  color: #999999;
  line-height: 17px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.section-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #1f2329;
  transition: background 0.2s;

  &:hover {
    background: #f5f7fa;
  }

  &.active {
    background: #e8f4ff;
    color: #3370ff;

    i {
      color: #3370ff;
    }
  }

  i {
    font-size: 15px;
    color: #646a73;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.history-list {
  display: flex;
  flex-direction: column;
  .noMessages {
    text-align: center;
    margin-top: 73px;
    display: block;
    img {
      width: 50%;
      height: 50%;
    }
    .noMessagesText {
      font-size: 12px;
      color: #999999;
      line-height: 18px;
      margin-top: 8px;
    }
    .noMessagesBtn {
      cursor: pointer;
      font-size: 12px;
      color: #4A5FF3;
      line-height: 14px;
      padding: 5px 16px;
      border: 1px solid #4A5FF3;
      border-radius: 4px;
      margin-top: 24px;
      display: inline-block;
    }
  }
}

.history-item {
  height: 36px;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #333333;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;

  &:hover {
    // background: #f7f8fc;
    background: rgba(156, 167, 248, 0.2);
  }

  &.active {
    background: #f3f6ff;
    color: #4a5ff3;
  }

  .session-agent-badge {
    font-size: 11px;
    color: #4a5ff3;
    background: #eef1fd;
    padding: 0 6px;
    border-radius: 3px;
    flex-shrink: 0;
    font-weight: 500;
    line-height: 18px;
  }

  .session-title {
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .session-more {
    flex-shrink: 0;
    font-size: 14px;
    color: #8f959e;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .session-more {
    opacity: 1;
  }

  .session-checkbox {
    flex-shrink: 0;
    margin: 0;
    pointer-events: none;
  }
  .session-checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    margin-left: -6px;
    margin-right: 2px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .session-checkbox-wrapper:hover {
    background: rgba(74, 95, 243, 0.08);
  }
  .session-checkbox-wrapper.checked {
    background: rgba(74, 95, 243, 0.12);
  }
  .session-checkbox.el-checkbox {
    padding: 0;
    margin: 0;
  }
  .session-checkbox ::v-deep .el-checkbox__label {
    display: none;
  }
  .session-checkbox ::v-deep .el-checkbox__inner {
    width: 14px;
    height: 14px;
  }
  .session-checkbox ::v-deep .el-checkbox__inner::after {
    left: 4px;
    top: 1px;
  }
}

.select-all-checkbox {
  margin-right: 4px;
}
.select-all-checkbox ::v-deep .el-checkbox__label {
  font-size: 12px;
  color: #595959;
  padding-left: 6px;
}
.select-all-checkbox ::v-deep .el-checkbox__inner {
  width: 13px;
  height: 13px;
}

.sessions-loading-icon {
  font-size: 13px;
  color: #8f959e;
}

.history-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 13px;
  color: #8f959e;

  i {
    font-size: 14px;
  }
}

.history-list {
  position: relative;
}

.history-list-mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: #8f959e;
  border-radius: 4px;

  i {
    font-size: 14px;
  }
}

.history-items-disabled {
  pointer-events: none;
  opacity: 0.5;
}

.session-edit-input {
  flex: 1;
  min-width: 0;
  border: 1px solid #3370ff;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  outline: none;
}

.sidebar-footer {
  padding: 8px;
  border-top: 1px solid #e8eaef;
}

/* ========== 左侧菜单 屏幕适配 ========== */
@media screen and (max-width: 1559px) {
  .sidebar-header {
    padding: 20px 14px;
  }
  .sidebar-section {
    padding: 10px 14px;
  }
  .sidebar-card {
    height: 60px;
    padding: 10px;
    gap: 10px;
  }
}

@media screen and (max-width: 1439px) {
  .sidebar-header {
    padding: 16px 12px;
    gap: 12px;
  }
  .sidebar-section {
    padding: 8px 12px;
  }
  .sidebar-card {
    height: 56px;
    padding: 8px;
    gap: 8px;
  }
  .sidebar-card-title {
    font-size: 13px;
  }
  .sidebar-card-desc {
    font-size: 11px;
  }
  .sidebar-card-logo {
    width: 36px;
    height: 36px;
  }
  .sidebar-card-logo i {
    font-size: 18px;
  }
  .history-item {
    height: 32px;
    padding: 8px;
    font-size: 13px;
  }
  .menu-item {
    padding: 6px 8px;
    font-size: 12px;
  }
}

@media screen and (max-width: 1279px) {
  .sidebar-header {
    padding: 12px 10px;
    gap: 10px;
  }
  .sidebar-title {
    font-size: 11px;
  }
  .sidebar-section {
    padding: 6px 10px;
  }
  .sidebar-card {
    height: 50px;
    padding: 6px;
    gap: 6px;
  }
  .sidebar-card-title {
    font-size: 12px;
  }
  .sidebar-card-desc {
    font-size: 11px;
  }
  .sidebar-card-logo {
    width: 32px;
    height: 32px;
  }
  .sidebar-card-logo i {
    font-size: 16px;
  }
  .history-item {
    height: 28px;
    padding: 8px;
    font-size: 12px;
  }
  .menu-item {
    padding: 5px 6px;
    font-size: 12px;
  }
}

/* 菜单图标 */
.menu-icon-img {
  width: 14px;
  height: 14px;
  margin-right: 6px;
  vertical-align: middle;
  flex-shrink: 0;
}
.menu-card-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.disable-tag {
  font-size: 10px;
  color: #CCCCCC;
  line-height: 12px;
  background: #ffffffcc;
  border-radius: 8px;
  padding: 2px 4px;
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
